<div class="project-management-container">
  <div class="header">
    <h1>{{ pageTitle }}</h1>
    <p class="subtitle">{{ pageSubtitle }}</p>
    @if (canManageProjects()) {
      <button class="btn btn-primary" (click)="openCreateModal()">
        Create New Project
      </button>
    }
  </div>

  <!-- Informational Note -->
  @if (isGenericFlow && canManageProjects()) {
    <div class="info-note">
      <mat-icon>info</mat-icon>
      <span>You can do one analysis in background at a time.</span>
    </div>
  }

  <!-- Error Messages -->
  @if (errorMessage) {
    <div class="alert alert-error">
      {{ errorMessage }}
    </div>
  }

  <!-- Loading State -->
  @if (loading) {
    <div class="loading-container">
      <div class="spinner"></div>
      <p>Loading projects...</p>
    </div>
  }

  <!-- Projects Table -->
  @if (!loading) {
    <div class="table-container">
    <div class="table-header">
      <div class="table-info">
        <span>Total Projects: {{ totalProjects }}</span>
        <span>Page {{ currentPage }} of {{ totalPages }}</span>
      </div>
    </div>

    <table class="projects-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Description</th>
          <th>Market</th>
          <th>Status</th>
          <th>Created</th>
          <th>Created by</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        @for (project of projects; track project.id) {
          <tr class="project-row">
          <td>
            <div class="project-name">{{ project.name }}</div>
          </td>
          <td>
            <div class="project-description">{{ project.description }}</div>
          </td>
          <td>
            <span class="type-badge">
              {{ project.primary_country || 'Global' }}
            </span>
          </td>
          <td>
            <span class="status-badge" [class]="getStatusClass(project.status)">
              {{ getStatusDisplay(project.status) }}
            </span>
          </td>
          <td>{{ formatDate(project.created_at) }}</td>
          <td>
            <span class="created-by">{{ project.created_by_name || '—' }}</span>
          </td>
          <td>
            @if (canManageProjects()) {
              <div class="action-buttons">
                @if (isGenericFlow) {
                  <div style="display: flex; gap: 8px; align-items: center;">
                    <button 
                      class="btn btn-sm btn-success re-analysis-button" 
                      (click)="reAnalyzeProject(project)" 
                      [disabled]="hasActiveAnalysis()"
                      title="Run new analysis">
                      @if (isProjectAnalyzing(project.id)) {
                        <mat-spinner diameter="16" class="re-analysis-spinner"></mat-spinner>
                        @if (getAnalysisProgress(project.id) !== null) {
                          <span class="re-analysis-progress">{{ getAnalysisProgress(project.id) }}%</span>
                        }
                      }
                      <span class="re-analysis-text">{{ (project.projectdata_count || 0) > 0 ? 'Re-Analysis' : 'Analysis' }}</span>
                    </button>
                    @if (isProjectAnalyzing(project.id) && activeAnalysisStatus?.task_id) {
                      <button 
                        class="btn btn-sm btn-danger cancel-button" 
                        (click)="cancelAnalysis(project)"
                        [disabled]="cancelling"
                        title="Cancel analysis">
                        @if (isStoppingProject(project.id)) {
                          <mat-spinner diameter="16" class="cancel-spinner"></mat-spinner>
                        } @else {
                          <mat-icon class="cancel-icon">cancel</mat-icon>
                        }
                        <span class="cancel-text">{{ isStoppingProject(project.id) ? 'Stopping...' : 'Cancel' }}</span>
                      </button>
                    }
                    <!-- <button class="btn btn-sm btn-info" (click)="openHistoryDialog(project)" title="View previous analysis">
                      Previous-Analysis
                    </button> -->
                    @if (shouldShowBrandDashboardButton(project)) {
                      <button class="btn btn-sm btn-info" (click)="goToBrandDashboard(project)" title="Open Brand Dashboard">
                        View Dashboard
                      </button>
                    }
                  </div>
                } @else {
                  <button class="btn btn-sm btn-success" (click)="analyzeProject(project)" title="Analyze this university">
                    Analysis
                  </button>
                }
                <button class="btn btn-sm btn-primary" (click)="openEditModal(project)" title="Edit project">
                  Edit
                </button>
                @if (isAdmin() && isAdminView) {
                  <button class="btn btn-sm btn-secondary" (click)="openStatusModal(project)">
                    Change Status
                  </button>
                  <button
                    class="btn btn-sm btn-secondary"
                    type="button"
                    (click)="openReassignOwnerDialog(project)"
                    title="Assign this project to another user"
                  >
                    Re-assign
                  </button>
                }
                <button class="btn btn-sm btn-danger" (click)="openDeleteModal(project)">
                  Delete
                </button>
              </div>
            }
          </td>
        </tr>
        }
      </tbody>
    </table>

    <!-- Pagination -->
    @if (totalPages > 1) {
      <div class="pagination">
        <button class="btn btn-sm" [disabled]="currentPage === 1" (click)="onPageChange(currentPage - 1)">
          Previous
        </button>

        <span class="page-info">
          Page {{ currentPage }} of {{ totalPages }}
        </span>

        <button class="btn btn-sm" [disabled]="currentPage === totalPages" (click)="onPageChange(currentPage + 1)">
          Next
        </button>
      </div>
    }
    </div>
  }

  <!-- Delete Project Modal -->
  @if (showDeleteModal) {
    <div class="modal-overlay" (click)="closeModals()">
    <div class="modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>Delete Project</h3>
        <button class="close-btn" (click)="closeModals()">&times;</button>
      </div>

      <div class="modal-body">
        <p>Are you sure you want to delete the project <strong>{{ selectedProject?.name }}</strong>?</p>
        <p class="warning-text">This action cannot be undone.</p>

        <div class="modal-actions">
          <button type="button" class="btn btn-secondary" (click)="closeModals()">Cancel</button>
          <button type="button" class="btn btn-danger" [disabled]="saving" (click)="deleteProject()">
            {{ saving ? 'Deleting...' : 'Delete Project' }}
          </button>
        </div>
      </div>
    </div>
    </div>
  }

  <!-- Change Status Modal -->
  @if (showStatusModal) {
    <div class="modal-overlay" (click)="closeModals()">
    <div class="modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>Change Project Status</h3>
        <button class="close-btn" (click)="closeModals()">&times;</button>
      </div>

      <div class="modal-body">
        <form (ngSubmit)="updateProjectStatus()">
          <div class="form-group">
            <label for="status">Status</label>
            <select id="status" [(ngModel)]="statusForm.status" name="status" required>
              @for (status of availableStatuses; track status.value) {
                <option [value]="status.value">
                  {{ status.label }}
                </option>
              }
            </select>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" (click)="closeModals()">Cancel</button>
            <button type="submit" class="btn btn-primary" [disabled]="saving">
              {{ saving ? 'Updating...' : 'Update Status' }}
            </button>
          </div>
        </form>
      </div>
    </div>
    </div>
  }
</div>
